class GiftWithPurchase extends HTMLElement {
  get selectors() {
    return {
      configuration: "#GiftWithPurchaseConfiguration",
      cart: "cart-section",
      drawer: "cart-drawer"
    }
  }

  get conditionType() {
    return {
      minQuantity: "min_quantity",
      minAmount: "min_amount"
    }
  }

  get conditionSource() {
    return {
      products: "products",
      collection: "collection"
    }
  }

  get gwpSource() {
    return {
      products: "products",
      collection: "collection"
    }
  }

  get routes() {
    return {
      cart: `${Shopify.routes.root}cart.js`,
      cartChange: `${Shopify.routes.root}cart/change.js`
    }
  }

  get modifiers() {
    return {
      loading: "cart-promo--loading",
      hidden: "hidden"
    }
  }

  constructor() {
    super();
  }

  async connectedCallback() {
    this.startModuleLoading();

    this.configuration = await this.getConfiguration();

    this.cartElement = document.querySelector(this.selectors.cart) ?? document.querySelector(this.selectors.drawer);

    this.cart = await this.fetchCart();

    const { doesCartMeetConditions, giftsAddedToCartCount, canAddMoreGifts } = await this.checkCartEligibility();

    if (!doesCartMeetConditions) {
      await this.removeGiftProductsFromCart();
      this.hideModule();
    } else if (canAddMoreGifts) {
      this.showModule();
    } else {
      this.hideModule();
    }

    this.endModuleLoading();
  }

  async checkCartEligibility() {
    let giftsAddedToCartCount = 0;

    let eligibleProductsAddedToCartCount = 0;
    let eligibleProductsAddedToCartPrice = 0;

    this.cart.items.forEach((item) => {
      if (this.configuration.gwpProducts.some((productId) => productId === item.product_id) && item.final_line_price === 0 && item.properties["_isGiftWithPurchase"]) {
        giftsAddedToCartCount += item.quantity;
        return;
      }

      if (this.configuration.conditionProducts.some((productId) => parseInt(productId) === parseInt(item.product_id))) {
        if (this.configuration.conditionVariants.length) {
          if (!this.configuration.conditionVariants.some((variantId) => parseInt(variantId) === parseInt(item.variant_id))) {
            return;
          }
        }

        eligibleProductsAddedToCartCount += item.quantity;
        eligibleProductsAddedToCartPrice += item.line_price;
      }
    });

    let doesCartMeetConditions = false;
    let potentialGiftGroups = 0;

    switch (this.configuration.conditionType) {
      case this.conditionType.minQuantity: {
        if (eligibleProductsAddedToCartCount >= this.configuration.minQuantity) {
          doesCartMeetConditions = true;
          potentialGiftGroups = Math.floor(eligibleProductsAddedToCartCount / this.configuration.minQuantity);
        }

        break;
      }

      case this.conditionType.minAmount: {
        if (eligibleProductsAddedToCartPrice >= this.configuration.minAmount) {
          doesCartMeetConditions = true;
          potentialGiftGroups = Math.floor(eligibleProductsAddedToCartPrice / this.configuration.minAmount);
        }

        break;
      }
    }

    let maxPossibleGifts = potentialGiftGroups * this.configuration.gwpQuantity;

    if (this.configuration.maxRewardsPerOrder > 0) {
      maxPossibleGifts = Math.min(maxPossibleGifts, this.configuration.maxRewardsPerOrder);
    }

    const canAddMoreGifts = giftsAddedToCartCount < maxPossibleGifts;

    return { doesCartMeetConditions, giftsAddedToCartCount, canAddMoreGifts };
  }

  async getConfiguration() {
    const configuration = JSON.parse(this.querySelector(this.selectors.configuration).innerHTML);

    if (configuration.conditionSource === this.conditionSource.collection) {
      configuration.conditionProducts = await this.fetchCollectionProductsIds(configuration.conditionCollection);
    }

    if (configuration.gwpSource === this.gwpSource.collection) {
      configuration.gwpProducts = await this.fetchCollectionProductsIds(configuration.gwpCollection);
    }

    return configuration;
  }

  showModule() {
    this.classList.remove(this.modifiers.hidden);
  }

  hideModule() {
    this.remove();
  }

  startModuleLoading() {
    this.classList.add(this.modifiers.loading);
  }

  endModuleLoading() {
    this.classList.remove(this.modifiers.loading);
  }

  async fetchCollectionProductsIds(handle) {
    const productIds = [];

    let page = 1;
    let hasMoreProducts = true;

    while (hasMoreProducts) {
      const url = `/collections/${handle}/products.json?limit=250&page=${page}`;

      const data = await window.cachedFetch(url, false);

      productIds.push(...data.products.map(product => product.id));

      hasMoreProducts = data.products.length === 250;
      page++;
    }

    return productIds;
  }

  async fetchCart() {
    return await (await fetch(this.routes.cart)).json();
  }

  async removeGiftProductsFromCart() {
    const giftWithPurchaseItems = this.cart.items.filter((item) => item.properties["_isGiftWithPurchase"]);

    if (!giftWithPurchaseItems.length) {
      return;
    }

    const sectionsToRender = this.cartElement.sectionsToRender ?? [];

    for (const item of giftWithPurchaseItems) {
      try {
        const response = await fetch(this.routes.cartChange, {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            id: item.key,
            quantity: 0,
            sections: sectionsToRender
          })
        });

        await response.json();
      } catch (error) {
        console.error(error);
      }
    }

    document.dispatchEvent(new CustomEvent("cart:change"));
  }
}

if (!customElements.get("gift-with-purchase")) {
  customElements.define("gift-with-purchase", GiftWithPurchase);
}
